import { useState, useEffect, useCallback, Suspense } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { redirectApi, REDIRECT_TYPES } from '../utils/api/redirects';
import { ROUTABLE_COMPONENTS, isRoutableComponent } from '../components/RoutableComponents';
import { useSpaces } from '../contexts/SpacesContext';
import PageViewer from '../components/PageViewer';

/**
 * Hook that handles URL redirects
 * 
 * Checks if the current path has a redirect configured and executes the appropriate action:
 * - SPACE: Navigate to the space
 * - PLUGIN: Launch the plugin
 * - PAGE: Display page in full-screen viewer
 * - COMPONENT: Open the component as a modal
 * - URL: Open in new tab
 */
const useRedirectHandler = ({ launchApp, apps }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { spaces, setActiveSpaceId } = useSpaces();
  const [modalComponent, setModalComponent] = useState(null);
  const [modalProps, setModalProps] = useState({});
  const [pageViewerSlug, setPageViewerSlug] = useState(null);
  const [isChecking, setIsChecking] = useState(false);
  const [checkedPaths, setCheckedPaths] = useState(new Set());
  const [pendingRedirect, setPendingRedirect] = useState(null);

  const closeModal = useCallback(() => {
    setModalComponent(null);
    setModalProps({});
    // Navigate back to root or previous location
    navigate('/', { replace: true });
  }, [navigate]);

  const closePageViewer = useCallback(() => {
    setPageViewerSlug(null);
    navigate('/', { replace: true });
  }, [navigate]);

  const handleRedirect = useCallback(async (redirect) => {
    const { target_type, target_value } = redirect;

    switch (target_type) {
      case REDIRECT_TYPES.SPACE: {
        // Find space by slug/name or space_id
        const targetLower = target_value.toLowerCase();
        const space = spaces.find(s => {
          const slug = s.name.toLowerCase().replace(/\s+/g, '-');
          return slug === targetLower || 
                 s.name.toLowerCase() === targetLower ||
                 s.space_id.toString() === target_value;
        });
        
        if (space) {
          setActiveSpaceId(space.space_id);
          const slug = space.name.toLowerCase().replace(/\s+/g, '-');
          navigate(`/spaces/${slug}`, { replace: true });
        } else {
          console.warn('Space not found for redirect:', target_value, 'Available:', spaces.map(s => s.name));
        }
        break;
      }

      case REDIRECT_TYPES.PLUGIN: {
        // Launch plugin by ID
        const app = apps?.find(a => a.id === target_value || a.plugin_id?.toString() === target_value);
        if (app) {
          launchApp?.(app);
          navigate(`/plugins/${target_value}`, { replace: true });
        }
        break;
      }

      case REDIRECT_TYPES.PAGE: {
        // Open page in full-screen viewer
        setPageViewerSlug(target_value);
        break;
      }

      case REDIRECT_TYPES.COMPONENT: {
        // Open component as modal
        if (isRoutableComponent(target_value)) {
          setModalComponent(target_value);
          setModalProps({ onClose: closeModal });
        } else {
          console.warn('Unknown routable component:', target_value);
        }
        break;
      }

      case REDIRECT_TYPES.URL: {
        // External redirect
        window.open(target_value, '_blank');
        navigate('/', { replace: true });
        break;
      }

      default:
        console.warn('Unknown redirect type:', target_type);
    }
  }, [spaces, apps, launchApp, navigate, setActiveSpaceId, closeModal]);

  // Check for redirects when path changes
  useEffect(() => {
    const path = location.pathname;

    // Skip routes that are already handled by React Router (except root which can have redirects)
    if (path.startsWith('/spaces/') || path.startsWith('/plugins/')) return;
    
    // Skip if already checking or already successfully handled this path
    if (isChecking) return;
    if (checkedPaths.has(path)) return;

    const checkRedirect = async () => {
      setIsChecking(true);
      try {
        const result = await redirectApi.lookup(path);
        if (result.found && result.redirect) {
          const redirect = result.redirect;
          
          // PLUGIN type needs apps to be loaded first
          if (redirect.target_type === REDIRECT_TYPES.PLUGIN) {
            if (!apps || apps.length === 0) {
              // Store pending redirect to execute when apps load
              setPendingRedirect(redirect);
              setIsChecking(false);
              return;
            }
          }
          
          // SPACE type needs spaces to be loaded first
          if (redirect.target_type === REDIRECT_TYPES.SPACE) {
            if (!spaces || spaces.length === 0) {
              // Store pending redirect to execute when spaces load
              setPendingRedirect(redirect);
              setIsChecking(false);
              return;
            }
          }
          
          await handleRedirect(redirect);
          setCheckedPaths(prev => new Set([...prev, path]));
        } else {
          // No redirect found - mark as checked so we don't keep trying
          setCheckedPaths(prev => new Set([...prev, path]));
        }
      } catch (error) {
        console.error('Error checking redirect:', error);
        // On error, don't mark as checked - allow retry
      } finally {
        setIsChecking(false);
      }
    };

    checkRedirect();
  }, [location.pathname, checkedPaths, isChecking, handleRedirect, apps, spaces]);

  // Handle pending redirects when apps or spaces load
  useEffect(() => {
    if (!pendingRedirect) return;
    
    const canExecute = 
      (pendingRedirect.target_type === REDIRECT_TYPES.PLUGIN && apps && apps.length > 0) ||
      (pendingRedirect.target_type === REDIRECT_TYPES.SPACE && spaces && spaces.length > 0) ||
      (pendingRedirect.target_type !== REDIRECT_TYPES.PLUGIN && pendingRedirect.target_type !== REDIRECT_TYPES.SPACE);
    
    if (canExecute) {
      handleRedirect(pendingRedirect);
      setCheckedPaths(prev => new Set([...prev, location.pathname]));
      setPendingRedirect(null);
    }
  }, [apps, spaces, pendingRedirect, handleRedirect, location.pathname]);

  // Render modal component if open
  const renderModal = useCallback(() => {
    if (!modalComponent) return null;

    const Component = ROUTABLE_COMPONENTS[modalComponent];
    if (!Component) return null;

    return (
      <Suspense fallback={<div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
        <div className="text-white">Loading...</div>
      </div>}>
        <Component {...modalProps} />
      </Suspense>
    );
  }, [modalComponent, modalProps]);

  // Render page viewer if open
  const renderPageViewer = useCallback(() => {
    if (!pageViewerSlug) return null;

    return (
      <PageViewer 
        pageSlug={pageViewerSlug} 
        onClose={closePageViewer} 
      />
    );
  }, [pageViewerSlug, closePageViewer]);

  return {
    modalComponent,
    closeModal,
    renderModal,
    pageViewerSlug,
    closePageViewer,
    renderPageViewer,
    isChecking
  };
};

export default useRedirectHandler;

